import { Select, Space, Table, Tag, Tooltip } from 'antd'
import { ItemsContext } from '../ItemsRoot'
import { useContext, useEffect, useState } from 'react'
import { ServerState, ViewItemFilter, createViewItems } from '../itemModel'
import { WorkareaItemTypes } from '../../../common/constants'
import ItemTable from '../ItemTable'
import { ItemGrouping, groupItems } from '../grouping'
import AddAnotherButton from '../AddAnotherButton'
import { CatalogItem } from '../../../domain/catalogItem'
import { centsToDollarsDisplay } from '../../displayFormatters'
import { green, red } from '@ant-design/colors'
import { QuestionCircleTwoTone } from '@ant-design/icons'
import Units from '../Units'
import { ColumnType } from 'antd/lib/table'
import { sum } from '../../../helpers/array'
import { roundToPrecision } from '../../../helpers/math'
import { hasValue } from '../../../helpers/nullable'
import VSpace from '../../common/VSpace'
import ToggleCatalogBrowserButton from '../../ItemCatalog/ToggleCatalogBrowserButton'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown, faChevronRight, faCirclePause } from '@fortawesome/pro-solid-svg-icons'
import { faCircleExclamation } from '@fortawesome/pro-duotone-svg-icons'
import SuperUserItemCalculations from '../ItemCalculations/SuperUserItemCalculations'

const CostingAnalysisItems = () => {
  const { jobItemsQuery, changeSet, jobData, focusedView, lastAddedCatalogItem } = useContext(ItemsContext)

  const [selectedWorkareaId, setSelectedWorkareaId] = useState(-1)
  const workareaId = selectedWorkareaId !== -1 ? selectedWorkareaId : undefined

  const [expandedRowKey, setExpandedRowKey] = useState<string>('')

  useEffect(() => {
    if (lastAddedCatalogItem) {
      setExpandedRowKey(`${lastAddedCatalogItem.catalogItem.guid}-${lastAddedCatalogItem.name ?? lastAddedCatalogItem.catalogItem.name}`)
    }
  }, [lastAddedCatalogItem])

  if (focusedView !== 'costing-and-analysis' && focusedView !== 'receipt') {
    return <></>
  }

  const estimatedItems = jobItemsQuery.data?.estimatedItems ?? []
  const estimatedItemsServerState: ServerState = {
    workareaItems: estimatedItems,
    catalogItems: jobItemsQuery.data?.catalogItems ?? [],
    templateItems: []
  }

  const actualItems = jobItemsQuery.data?.actualItems ?? []
  const actualItemsServerState: ServerState = {
    workareaItems: actualItems,
    catalogItems: jobItemsQuery.data?.catalogItems ?? [],
    templateItems: []
  }

  const workareaIds = hasValue(workareaId)
    ? [workareaId]
    : jobData.workareas.filter(x => !x.isOnHold).map(x => x.id)

  const filter: ViewItemFilter = {
    workareaItemType: WorkareaItemTypes.Actual,
    workareaIds
  }
  const estimatedViewItems = createViewItems(changeSet, estimatedItemsServerState, jobData.visitData, jobData.budgets,
    jobData.jobIsSold, jobData.workareas, { ...filter, workareaItemType: WorkareaItemTypes.Estimated })
    .filter(x => !x.isEmptyRow && workareaIds.includes(x.workareaId))
  const actualViewItems = createViewItems(changeSet, actualItemsServerState, jobData.visitData, jobData.budgets,
    jobData.jobIsSold, jobData.workareas, { ...filter, workareaItemType: WorkareaItemTypes.Actual })
    .filter(x => !x.isEmptyRow && workareaIds.includes(x.workareaId))

  const groupedItems = groupItems(estimatedViewItems, actualViewItems, actualItemsServerState.catalogItems, jobData.workareas)

  const workareaOptions = [
    { label: 'All Workareas', value: -1 },
    ...jobData.workareas.map(w => ({
      label: <Space>{w.name}{w.isOnHold && <Tooltip title="On Hold"><FontAwesomeIcon icon={faCirclePause} /></Tooltip>}</Space>,
      value: w.id,
    }))
  ]

  const workareaSelector = <Select
    key="workarea-selector"
    size="large"
    options={workareaOptions}
    value={selectedWorkareaId}
    onChange={value => setSelectedWorkareaId(value)}
    style={{ minWidth: 200 }}
    popupMatchSelectWidth={false}
    showSearch={true}
    optionFilterProp="label"
    filterOption={(input, option) => {
      const workarea = jobData.workareas.find(x => x.id === option?.value)
      return workarea?.name.toLowerCase().includes(input.toLowerCase()) ?? false
    }}
  />

  const findCatalogItem = (group: ItemGrouping): CatalogItem | undefined => {
    return estimatedItemsServerState.catalogItems.find(x => x.guid === group.catalogItemGuid)
      ?? actualItemsServerState.catalogItems.find(x => x.guid === group.catalogItemGuid)
      ?? changeSet.catalogItemsForAddedItems.find(x => x.guid === group.catalogItemGuid)
  }

  type CostingAnalysisColumn = ColumnType<ItemGrouping> & {
    renderSummary?: () => React.ReactNode
  }

  const differenceStyle = (difference: number) => {
    let color = 'inherit'
    let fontWeight = 'inherit'
    if (difference < 0) {
      color = red.primary ?? ''
      fontWeight = 'bold'
    } else if (difference > 0) {
      color = green.primary ?? ''
    }
    return { color, fontWeight }
  }

  const columns: CostingAnalysisColumn[] = [
    {
      render: (_, group) => {
        const catalogItem = findCatalogItem(group)
        return <Space>
          <AddAnotherButton item={catalogItem} name={group.name}
            workareaId={selectedWorkareaId === -1 ? undefined : selectedWorkareaId}
            onItemAdded={() => setExpandedRowKey(`${catalogItem?.guid}-${group.name}`)}
          />
          {group.actualQuantity === 0 && group.estimatedQuantity > 0 &&
            <Tooltip title="No actuals have been recorded for this estimated item">
              <QuestionCircleTwoTone twoToneColor={green.primary} style={{ fontSize: 16 }} />
            </Tooltip>
          }
          {group.actualQuantity > 0 && group.estimatedQuantity === 0 &&
            <Tooltip title="This actual item was not included in the estimate">
              <FontAwesomeIcon icon={faCircleExclamation} style={{ color: red.primary, fontSize: 16 }} />
            </Tooltip>
          }
        </Space>
      }
    },
    {
      title: 'Item',
      dataIndex: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      defaultSortOrder: 'ascend'
    },
    {
      title: 'Estimated Quantity',
      align: 'right',
      sorter: (a, b) => a.estimatedQuantity - b.estimatedQuantity,
      render: (_, item) => <>{roundToPrecision(item.estimatedQuantity, 2)} <Units units={item.units} maxWidth={'100px'} /></>
    },
    {
      title: 'Actual Quantity',
      align: 'right',
      sorter: (a, b) => a.actualQuantity - b.actualQuantity,
      render: (_, item) => <>{roundToPrecision(item.actualQuantity, 2)} <Units units={item.units} maxWidth={'100px'} /></>,
      onCell: (g) => {
        const isOver = g.actualQuantity > g.estimatedQuantity
        return {
          style: {
            fontWeight: isOver ? 'bold' : 'inherit',
            color: isOver ? red.primary : 'inherit'
          }
        }
      }
    },
    {
      title: 'Estimated Cost',
      dataIndex: 'estimatedTotalCost',
      sorter: (a, b) => a.estimatedTotalCost - b.estimatedTotalCost,
      render: centsToDollarsDisplay,
      align: 'right',
      renderSummary: () => centsToDollarsDisplay(sum(groupedItems, x => x.estimatedTotalCost))
    },
    {
      title: 'Actual Cost',
      dataIndex: 'actualTotalCost',
      sorter: (a, b) => a.actualTotalCost - b.actualTotalCost,
      render: centsToDollarsDisplay,
      align: 'right',
      renderSummary: () => centsToDollarsDisplay(sum(groupedItems, x => x.actualTotalCost))
    },
    {
      title: '+/-',
      dataIndex: 'difference',
      onCell: g => ({ style: differenceStyle(g.difference) }),
      sorter: (a, b) => a.difference - b.difference,
      render: centsToDollarsDisplay,
      align: 'right',
      renderSummary: () => {
        const totalDifference = sum(groupedItems, x => x.difference)
        return <span style={differenceStyle(totalDifference)}>{centsToDollarsDisplay(totalDifference)}</span>
      }
    },
    {
      title: 'Total Price',
      dataIndex: 'totalActualPrice',
      sorter: (a, b) => a.totalActualPrice - b.totalActualPrice,
      render: centsToDollarsDisplay,
      align: 'right',
      renderSummary: () => centsToDollarsDisplay(sum(groupedItems, x => x.totalActualPrice))
    }
  ]

  return <VSpace>
    <Space>
      {workareaSelector}
      <ToggleCatalogBrowserButton />
      <SuperUserItemCalculations />
    </Space>
    <Table
      dataSource={groupedItems}
      loading={jobItemsQuery.isLoading}
      rowKey="key"
      columns={columns}
      expandable={{
        expandedRowRender: (group) => <ItemTable
          viewItems={group.actualItems}
          workareaItemType="actual"
          budgets={jobData.budgets}
          serverState={actualItemsServerState}
          isLoading={false}
          visibleColumns={['visitDetails', 'quantity', 'isBillable', 'cost', 'totalCost', 'totalPrice', 'actions']}
          workareas={jobData.workareas}
          customSummary={() => <></>}
          showVisitWorkareaName={selectedWorkareaId === -1}
          quantityTitleOverride='Actual Quantity'
        />,
        expandedRowKeys: [expandedRowKey],
        onExpand: (expanded, item) => setExpandedRowKey(expanded ? item.key : ''),
        rowExpandable: (record) => record.actualItems.length > 0,
        expandIcon: ({ expandable, expanded, onExpand, record }) => {
          if (!expandable) {
            return <></>
          }
          const icon = expanded ? faChevronDown : faChevronRight
          const style = { cursor: 'pointer' }
          return <Tag onClick={e => onExpand(record, e)} style={style}>
            <FontAwesomeIcon icon={icon} /> &nbsp;{record.actualItems.length}
          </Tag>
        }
      }}
      summary={() => {
        return <Table.Summary.Row style={{ fontWeight: 'bold' }}>
          <Table.Summary.Cell index={0} />
          {columns.map((x, i) => <Table.Summary.Cell index={i + 1} key={i + 1} align={x.align}>
            {x.renderSummary?.()}
          </Table.Summary.Cell>)}
        </Table.Summary.Row>
      }}
      pagination={{
        pageSize: 50,
        hideOnSinglePage: true,
        showSizeChanger: false
      }}
      showSorterTooltip={false}
    />
  </VSpace>
}
export default CostingAnalysisItems
